import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Users } from 'lucide-react';
import type { UserActivityItem } from '../../../lib/api';
import { ChartEmptyState } from './ChartEmptyState';

interface PeopleTabProps {
  users: UserActivityItem[];
}

export function PeopleTab({ users }: PeopleTabProps) {
  const chartData = [...users]
    .sort((a, b) => b.active_days - a.active_days)
    .slice(0, 15)
    .map((u) => ({
      name: truncateLabel(u.name || u.email, 16),
      fullLabel: u.name || u.email,
      days: u.active_days,
      areas: u.unique_features,
      company: u.company,
    }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-5 w-5" />
            Most regular users
          </CardTitle>
          <CardDescription>People who came back on the most separate days during the period.</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <ChartEmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v: number) => [v, 'Days active']}
                  labelFormatter={(_, payload) => {
                    const row = payload?.[0]?.payload as { fullLabel?: string; company?: string } | undefined;
                    return row ? `${row.fullLabel} (${row.company})` : '';
                  }}
                />
                <Bar dataKey="days" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Everyone active this period</CardTitle>
          <CardDescription>Sorted by how many different portal areas each person visited.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">Person</th>
                  <th className="pb-2 font-medium">Organisation</th>
                  <th className="pb-2 font-medium text-right">Areas used</th>
                  <th className="pb-2 font-medium text-right">Days active</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="py-2.5">
                      <div className="font-medium">{user.name || user.email}</div>
                      {user.name && <div className="text-xs text-muted-foreground">{user.email}</div>}
                    </td>
                    <td className="py-2.5 text-xs">{user.company}</td>
                    <td className="py-2.5 text-right">{user.unique_features}</td>
                    <td className="py-2.5 text-right">{user.active_days}</td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted-foreground py-10">
                      No individual activity for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function truncateLabel(label: string, max: number): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}
